import { useEffect, useRef, useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Magnetic from './ui/Magnetic';
import { TextReveal } from './ui/TextReveal';

const slides = [
  {
    image: '/images/hero-lcp.webp',
    video: '/videos/hero-1.mp4',
    badge: '24 ANOS DE HISTÓRIA',
    title: 'Do resíduo ao produto.\nDo produto ao progresso.',
    description:
      'Tubos de PVC fabricados em Itaúna (MG) com material 100% reciclado para construção, irrigação e saneamento.',
    ctas: [{ label: 'Conheça os produtos', href: '#segmentos', primary: true }],
  },
  {
    image: '/images/hero-2.webp',
    badge: 'PRODUTO ECOLOGICAMENTE CORRETO',
    title: 'Soluções em tubos\nque constroem o Brasil\ne respeitam o planeta.',
    description:
      'Reaproveitamos resíduos de PVC rígido para transformá-los em tubos de alta qualidade para todo o país.',
    ctas: [
      { label: 'Nossa história', href: '#quem-somos', primary: true },
      { label: 'Solicitar Orçamento', href: '#contato', primary: false },
    ],
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
  exit: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 as const },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
  exit: {
    opacity: 0,
    y: -16,
    transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] as const },
  },
};

export const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [playingVideoIndex, setPlayingVideoIndex] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const go = (idx: number) => setCurrent((idx + slides.length) % slides.length);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => setCurrent((index) => (index + 1) % slides.length), 7000);
  };

  const attemptVideoPlayback = (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', 'true');
    video.setAttribute('playsinline', 'true');

    const playAttempt = video.play();
    if (playAttempt?.catch) {
      playAttempt.catch(() => {
        setPlayingVideoIndex(null);
      });
    }
  };

  useEffect(() => {
    resetInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    setPlayingVideoIndex(null);

    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index !== current) {
        video.pause();
        return;
      }

      attemptVideoPlayback(index);
    });
  }, [current]);

  const handleNav = (dir: 1 | -1) => {
    go(current + dir);
    resetInterval();
  };

  const slide = slides[current];

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden bg-cinza-escuro group">
      {/* Área de arraste (Drag) para Mobile */}
      <m.div
        className="absolute inset-0 z-20 cursor-grab active:cursor-grabbing"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={(_, info) => {
          const swipeThreshold = 50;
          if (info.offset.x < -swipeThreshold) {
            handleNav(1);
          } else if (info.offset.x > swipeThreshold) {
            handleNav(-1);
          }
        }}
      >
        {/* Textura de pontos */}
        <div className="absolute inset-0 z-[5] pointer-events-none hero-dots" />

        {/* Slides - imagens/vídeo com fade CSS */}
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 h-full w-full transition-opacity duration-1000 ${
              i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {s.video ? (
              <>
                <video
                  ref={(element) => {
                    videoRefs.current[i] = element;
                  }}
                  src={s.video}
                  poster={s.image}
                  className="absolute inset-0 h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  defaultMuted
                  playsInline
                  preload={i === current ? 'auto' : 'metadata'}
                  onCanPlay={() => {
                    if (i === current) attemptVideoPlayback(i);
                  }}
                  onLoadedData={() => {
                    if (i === current) attemptVideoPlayback(i);
                  }}
                  onPlaying={() => setPlayingVideoIndex(i)}
                />
                {/* Poster visível até o vídeo realmente entrar em reprodução */}
                <img
                  src={s.image}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
                    playingVideoIndex === i ? 'opacity-0' : 'opacity-100'
                  }`}
                  alt={`Tubos Canaã — ${s.badge}`}
                  fetchPriority={i === 0 ? 'high' : 'auto'}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  width={1920}
                  height={1080}
                />
              </>
            ) : (
              <img
                src={s.image}
                className="absolute inset-0 h-full w-full object-cover"
                alt={`Tubos Canaã — ${s.badge}: ${s.title.replace('\n', ' ')}`}
                fetchPriority={i === 0 ? 'high' : 'auto'}
                loading={i === 0 ? 'eager' : 'lazy'}
                width={1920}
                height={1080}
              />
            )}
          </div>
        ))}

        {/* Sombra localizada atrás do texto para legibilidade */}
        <div className="absolute z-[15] inset-x-0 bottom-0 h-[60%] pointer-events-none bg-gradient-to-t from-cinza-escuro/85 via-cinza-escuro/40 to-transparent" />
        <div className="absolute z-[15] inset-y-0 left-0 w-full md:w-2/3 pointer-events-none bg-gradient-to-r from-cinza-escuro/70 via-cinza-escuro/20 to-transparent" />

        {/* Conteúdo - AnimatePresence para stagger na troca de slide */}
        <div className="absolute z-20 inset-x-0 bottom-24 pointer-events-none">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pointer-events-auto">
            <AnimatePresence mode="wait">
              <m.div
                key={current}
                variants={containerVariants}
                initial={current === 0 ? false : 'hidden'}
                animate="visible"
                exit="exit"
                className="max-w-2xl text-branco"
              >
                {/* Badge */}
                <m.div
                  variants={itemVariants}
                  className="inline-flex bg-destaque-1 text-branco text-[10px] md:text-xs tracking-widest font-bold mb-5 px-3 py-1.5 rounded-sm shadow-md uppercase"
                >
                  {slide.badge}
                </m.div>

                {/* Título com contraste tipográfico */}
                <m.h1
                  variants={itemVariants}
                  className="mb-6 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.15] tracking-tight"
                  style={{ textShadow: '0 2px 16px rgba(0,0,0,0.65), 0 1px 3px rgba(0,0,0,0.5)' }}
                >
                  <TextReveal>
                    {slide.title.split('\n').map((line, j) => (
                      <span key={j} className="block">
                        {line}
                      </span>
                    ))}
                  </TextReveal>
                </m.h1>

                {/* Descrição */}
                <m.p
                  variants={itemVariants}
                  className="text-sm md:text-base mb-8 opacity-95 font-medium max-w-lg leading-relaxed"
                  style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {slide.description}
                </m.p>

                {/* CTAs */}
                <m.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
                  {slide.ctas.map((cta) => (
                    <Magnetic key={cta.label}>
                      <a
                        href={cta.href}
                        className={`btn-magnetic px-8 py-4 rounded-full font-black uppercase text-xs tracking-widest flex items-center justify-center transition-colors duration-300 ${
                          cta.primary
                            ? 'bg-verde-folha text-branco hover:bg-verde-folha/90'
                            : 'bg-transparent border border-branco/50 text-branco hover:bg-branco/10 hover:border-branco'
                        }`}
                      >
                        {cta.label}
                      </a>
                    </Magnetic>
                  ))}
                </m.div>
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </m.div>

      {/* Dots */}
      <div className="absolute z-30 bottom-10 left-0 right-0 flex justify-center gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => {
              go(i);
              resetInterval();
            }}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === current ? 'w-10 bg-verde-folha' : 'w-4 bg-branco/40'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Setas */}
      <div className="absolute z-30 inset-y-0 left-0 right-0 hidden md:flex items-center justify-between px-6 pointer-events-none">
        <button
          type="button"
          onClick={() => handleNav(-1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-branco/10 text-branco hover:bg-branco/20 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-xl"
          aria-label="Slide anterior"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => handleNav(1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-branco/10 text-branco hover:bg-branco/20 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-xl"
          aria-label="Próximo slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
};
